import type { QueryClient } from "@tanstack/react-query";
import type { TaxReturnDocument } from "./taxReturnSource";

const STORAGE_KEY = "taxReturnDraft:v1";
type Identity = { id: string; ownerUserId?: string | null; impersonatingUserId?: string | null };
export type TaxReturnDraft = { key: string; name: string; year: string };

export function taxReturnDraftScope(user: Identity | null | undefined) {
  return user ? JSON.stringify([user.id, user.impersonatingUserId ?? user.ownerUserId ?? user.id]) : "";
}

export function taxReturnDraftKey(scope: string, doc: TaxReturnDocument | null) {
  // Filename-only legacy records cannot distinguish a replacement with the
  // same name. Keep those edits local rather than attributing them to a later
  // document after navigation/reload.
  if (!scope || !doc || !(doc.sourceJobId || doc.cloudPath || doc.uploadedAt || doc.fileHash)) return "";
  return JSON.stringify([scope, doc.sourceJobId, doc.fileName,
    doc.cloudPath, doc.uploadedAt, doc.fileHash]);
}

export function readTaxReturnDraft(key: string): TaxReturnDraft | null {
  try {
    const draft = JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? "null");
    return key && draft?.key === key && typeof draft.name === "string" && typeof draft.year === "string"
      ? draft : null;
  } catch { return null; }
}

export function writeTaxReturnDraft(draft: TaxReturnDraft) {
  if (!draft.key) return;
  try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(draft)); } catch {}
}

export function clearTaxReturnDraft(key?: string) {
  try {
    if (key === undefined || readTaxReturnDraft(key)) sessionStorage.removeItem(STORAGE_KEY);
  } catch {}
}

// Observe identity even while Operations is unmounted. Undefined is a pending
// auth query, not logout; retain the session draft during a normal reload.
export function bindTaxReturnDraftSession(client: QueryClient) {
  let previous: string | undefined;
  return client.getQueryCache().subscribe(event => {
    if (event.query.queryKey[0] !== "/api/auth/me") return;
    if (event.type === "removed") {
      clearTaxReturnDraft();
      previous = "";
    } else if (event.type === "updated" && event.action.type === "success") {
      const scope = taxReturnDraftScope(event.query.state.data as Identity | null);
      try {
        const storedScope = sessionStorage.getItem(`${STORAGE_KEY}:scope`);
        if ((storedScope !== null && storedScope !== scope) || (previous !== undefined && previous !== scope)) {
          clearTaxReturnDraft();
        }
        sessionStorage.setItem(`${STORAGE_KEY}:scope`, scope);
      } catch {}
      previous = scope;
    }
  });
}
